import type { SkillListScope } from "../SkillRecord";

/**
 * Query key factory for skill queries. Two branches hang off the `["skills"]`
 * root, and which branch a key lives on decides what a mutation refreshes:
 *
 *  - `org(orgId)` — the org's skill *reads*: the scoped listings and the
 *    per-skill summaries. Every content mutation invalidates this one key, so a
 *    rename, a new version, a subscribe, or a delete refreshes both the list
 *    and any open detail page without each mutation hook having to remember a
 *    second key.
 *  - `orgTags(orgId)` and `versions(skillId)` — deliberately *outside*
 *    `org(orgId)`. The tag vocabulary only shifts when a changeset actually
 *    touches tags, and the version list only when versions change; hanging
 *    either under `org` would refetch it on every unrelated edit. Their owners
 *    invalidate them explicitly.
 *
 * Within `org(orgId)`, keys are hierarchical so a shorter key is a prefix of
 * its descendants: `list(orgId)` covers every scope's listing, and
 * `detail(orgId, skillId)` sits under `details(orgId)`.
 *
 * `orgId` and `skillId` are nullable because the hooks are mounted before auth
 * and route params resolve; those queries stay `enabled: false` until both are
 * defined, so an `undefined` segment never keys a real fetch.
 */
export const skillQueryKeys = {
  all: () => ["skills"] as const,

  // ── Org-scoped reads; every content mutation invalidates this subtree ────
  org: (orgId: string | undefined) => [...skillQueryKeys.all(), orgId] as const,
  list: (orgId: string | undefined, scope?: SkillListScope) =>
    scope === undefined
      ? ([...skillQueryKeys.org(orgId), "list"] as const)
      : ([...skillQueryKeys.org(orgId), "list", scope] as const),
  details: (orgId: string | undefined) =>
    [...skillQueryKeys.org(orgId), "summary"] as const,
  detail: (orgId: string | undefined, skillId: string | undefined) =>
    [...skillQueryKeys.details(orgId), skillId] as const,

  // ── Siblings of `org`, refreshed only by the edits that move them ────────
  orgTags: (orgId: string | undefined) =>
    [...skillQueryKeys.all(), "tags", orgId] as const,
  versions: (skillId: string | undefined) =>
    [...skillQueryKeys.all(), "versions", skillId] as const,
};
